import {
  ConfirmModal,
  Link,
  PageWrapper,
  StickyActionsHeader,
  USER_REPLACEMENT_DEFAULT_QUERY_PARAMS,
} from "@/components";
import * as GlobalS from "@/components/styled";
import { FixedWidthSpace } from "@/containers/Admin/UserReplacement/styled";
import { UserSelect } from "@/containers/Project/components";
import { GUTTER_SIZE } from "@/enums";
import { routes } from "@/helpers";
import { useToggle } from "@/hooks";
import {
  useGetDefaultRolesForUserQuery,
  useSubmitUserReplaceMutation,
  useUserReplacementSearchQuery,
} from "@/services";
import { Button, Col, Form, Row, Space } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useQueryParams } from "use-query-params";
import { ExportUserReplacementQuery } from "./ExportUserReplacementQuery";
import SearchFilter from "./SearchFilter";
import { SetAllowBlankToggle } from "./SetAllowBlankToggle";
import { SetNotApplicableToggle } from "./SetNotApplicableToggle";
import UserReplacementTable from "./UserReplacementTable";
import { insertProjectRowDataIntoTableData } from "./helpers";

const UserReplacement = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [replaceBy, setReplaceBy] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [showConfirm, toggleShowConfirm] = useToggle(false);
  const [selectedRowsData, setSelectedRowsData] = useState();
  const [naToggleValue, setNaToggleValue] = useState();
  const [blankToggleValue, setBlankToggleValue] = useState();
  const [replaceByReadOnly, setReplaceByReadonly] = useState(false);
  const [queryParams, setQueryParams] = useQueryParams({
    ...USER_REPLACEMENT_DEFAULT_QUERY_PARAMS,
  });

  const { data, isLoading } = useUserReplacementSearchQuery(queryParams, {
    skip: !queryParams?.userToBeReplaced,
  });

  const [submitUserReplace, { isLoading: isProcessing }] =
    useSubmitUserReplaceMutation();

  const { data: defaultRoles, isLoading: defaultRolesLoading } =
    useGetDefaultRolesForUserQuery(queryParams.userToBeReplaced);

  useEffect(() => {
    var formData = form.getFieldsValue();
    if (
      formData.userToBeReplaced !== queryParams.userToBeReplaced &&
      !queryParams.userToBeReplaced
    ) {
      form.setFieldsValue({
        userToBeReplaced: queryParams.userToBeReplaced,
        projectNumber: queryParams.projectNumber,
      });
      setReplaceBy(null);
    }
  }, [form, queryParams.projectNumber, queryParams.userToBeReplaced]);

  const handleUserSelectChange = (value) => {
    setReplaceBy(value);
  };

  const handleSearch = () => {
    setQueryParams((prevState) => ({ ...prevState, ...form.getFieldsValue() }));
  };

  const canSubmitReplace = () =>
    (selectedRows.length > 0 &&
      !!replaceBy &&
      replaceBy !== queryParams.userToBeReplaced) ||
    naToggleValue ||
    blankToggleValue;

  const handleReplaceSelected = () => {
    toggleShowConfirm();
  };

  const handleSubmit = () => {
    const request = {
      replaceBy: replaceBy,
      projectUserRoles: data.filter(
        (x) => selectedRows.includes(x.id) && !x.isProjectHeader
      ),
      userToBeReplaced: queryParams.userToBeReplaced,
      setNotApplicable: naToggleValue,
      setReplaceWithBlank: blankToggleValue,
    };
    submitUserReplace({ userReplaceRequest: request, queryParams, data })
      .unwrap()
      .then(() => {
        toggleShowConfirm();
        setSelectedRows([]);
        setSelectedRowsData(null);
        toast.success(intl.formatMessage({ id: "userReplacement.saved" }));
      })
      .catch((error) => {
        console.error(error);
        toast.error(intl.formatMessage({ id: "userReplacement.error" }));
      });
  };

  const hideAndResetReplaceUserWithField = useCallback((checked) => {
    if (checked) {
      setReplaceBy(undefined);
      setReplaceByReadonly(true);
    } else {
      setReplaceByReadonly(false);
    }
  }, []);

  const handleNaToggle = useCallback(
    (checked) => {
      setNaToggleValue(checked);
      if (checked) {
        setBlankToggleValue(false);
      }
      hideAndResetReplaceUserWithField(checked);
    },
    [hideAndResetReplaceUserWithField]
  );

  const handleBlankToggle = useCallback(
    (checked) => {
      setBlankToggleValue(checked);
      if (checked) {
        setNaToggleValue(false);
      }
      hideAndResetReplaceUserWithField(checked);
    },
    [hideAndResetReplaceUserWithField]
  );

  return (
    <>
      <PageWrapper
        pageTitle={intl.formatMessage({ id: "userReplacement.pageTitle" })}
        pageActions={
          <StickyActionsHeader
            rightChildren={
              <Space>
                {(data?.length > 0 || defaultRoles?.length > 0) &&
                  !isLoading &&
                  !defaultRolesLoading &&
                  queryParams?.userToBeReplaced && (
                    <ExportUserReplacementQuery
                      userReplacedId={queryParams.userToBeReplaced}
                      data={data}
                      defaultRolesData={defaultRoles}
                    />
                  )}

                <Button
                  data-test-id="user-replace-selected-btn"
                  type="primary"
                  block
                  disabled={!canSubmitReplace()}
                  onClick={handleReplaceSelected}
                >
                  {intl.formatMessage({
                    id: "userReplacement.replaceSelected",
                  })}
                </Button>
              </Space>
            }
          />
        }
      >
        <Row gutter={GUTTER_SIZE.large}>
          <GlobalS.PageContentCard
            title={intl.formatMessage({ id: "userReplacement.pageTitle" })}
            extra={
              <Link to={routes.admin.userReplacement.history}>
                {intl.formatMessage({ id: "general.history" })}
              </Link>
            }
          >
            <Row className="pb-2" gutter={GUTTER_SIZE.large}>
              <Col sm={24} md={12} flex={1}>
                <GlobalS.FullHeightCard
                  type="inner"
                  style={{ backgroundColor: "#fcecee" }}
                  title={intl.formatMessage({
                    id: "userReplacement.searchFor",
                  })}
                >
                  <SearchFilter
                    form={form}
                    onFinish={handleSearch}
                    initialValues={queryParams}
                  />
                </GlobalS.FullHeightCard>
              </Col>
              <Col sm={24} md={12} flex={1}>
                <GlobalS.FullHeightCard
                  type="inner"
                  style={{ backgroundColor: "#e9e9e9" }}
                  title={intl.formatMessage({
                    id: "userReplacement.replaceWith",
                  })}
                >
                  <Row gutter={GUTTER_SIZE.large} align="bottom">
                    <FixedWidthSpace direction="vertical" size="small">
                      {intl.formatMessage({
                        id: "userReplacement.replaceUserWith",
                      })}
                      <UserSelect
                        multipleSelect={false}
                        value={replaceBy}
                        onChange={handleUserSelectChange}
                        data-test-id="replace-by"
                        allowClear
                        readOnly={replaceByReadOnly}
                      />
                    </FixedWidthSpace>
                    <Col>
                      <SetNotApplicableToggle
                        selectedRowsData={selectedRowsData}
                        naToggleValue={naToggleValue}
                        handleNaToggle={handleNaToggle}
                      />
                    </Col>
                    <Col>
                      <SetAllowBlankToggle
                        selectedRows={selectedRowsData}
                        toggleValue={blankToggleValue}
                        handleToggle={handleBlankToggle}
                      />
                    </Col>
                  </Row>
                </GlobalS.FullHeightCard>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <UserReplacementTable
                  data={
                    !!queryParams.userToBeReplaced &&
                    insertProjectRowDataIntoTableData(data)
                  }
                  isLoading={isLoading}
                  queryParams={queryParams}
                  setQueryParams={setQueryParams}
                  setSelectedRowKeys={setSelectedRows}
                  selectedRowKeys={selectedRows}
                  setSelectedRowsData={setSelectedRowsData}
                />
              </Col>
            </Row>
          </GlobalS.PageContentCard>
        </Row>
      </PageWrapper>
      <ConfirmModal
        loading={isProcessing}
        onSaveChanges={handleSubmit}
        hidden={!showConfirm}
        toggle={toggleShowConfirm}
        headerTitleKey={"userReplacement.deleteModalTitle"}
        bodyTextKey={"userReplacement.deleteModalDescription"}
      />
    </>
  );
};

export default UserReplacement;
